/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/*
 * @format
 */
import React from 'react';
import PropTypes from 'prop-types';
import {
    Checkbox,
    Icon,
    FlexRow,
    FlexCol,
    Button,
    PhoneNumberField,
    TextField,
    StyledModal,
    Heading,
    Text,
    Stack,
} from 'sarsaparilla';
import get from 'lodash/get';
import set from 'lodash/set';
import AlternateGroupLeader from './AlternateGroupLeader';
import { getNewId } from '../../utils/utilsNew';

export default class GroupDetails extends React.Component {
    constructor() {
        super();

        this.state = {
            altLeaderCount: 0,
            showAddAlternateButton: true,
        };

        this.closeModal = this.closeModal.bind(this);
        this.openModal = this.openModal.bind(this);
        this.removeLeader = this.removeLeader.bind(this);
        this.handleAlternateLeaderChange = this.handleAlternateLeaderChange.bind(this);
        this.handleLeaderDetailsChange = this.handleLeaderDetailsChange.bind(this);
        this.addLeader = this.addLeader.bind(this);
    }

    componentDidUpdate(_, prevState) {
        if (!this.props.groupDetail || !this.props.groupDetail.altLeaders) return;

        if (
            prevState.altLeaderCount === this.state.altLeaderCount &&
            prevState.showAddAlternateButton === this.state.showAddAlternateButton
        ) {
            return;
        }

        this.setState({
            altLeaderCount: this.props.groupDetail.altLeaders.length,
            showAddAlternateButton:
                this.props.groupDetail.altLeaders.length < this.props.maxAlternateLeaders,
        });
    }

    handleLeaderDetailsChange(field, value) {
        const groupDetail = { ...this.props.groupDetail };
        set(groupDetail, field, value);
        this.props.handleGroupDetailChange(groupDetail);
    }

    handleAlternateLeaderChange(idx, field, input) {
        const newGroupDetail = this.props.groupDetail;
        const foundLeader = newGroupDetail.altLeaders[idx];

        if (foundLeader) {
            switch (field) {
                case 'other-email':
                    foundLeader.email = input;
                    break;
                default:
                    break;
            }
            this.props.handleGroupDetailChange(newGroupDetail);
        }
    }

    openModal() {
        this.setState({ modalIsOpen: true });
    }

    closeModal() {
        this.setState({
            modalIsOpen: false,
        });
    }

    addLeader() {
        const newGroupDetail = this.props.groupDetail;
        const currentCount = this.state.altLeaderCount;
        const condition = currentCount + 1 <= this.props.maxAlternateLeaders;
        if (condition) {
            const newLeader = {
                key: getNewId(),
                email: '',
                fName: '',
                mName: '',
                lName: '',
                phone: '',
            };
            newGroupDetail.altLeaders.push(newLeader);
        }
        this.props.handleGroupDetailChange(newGroupDetail);
    }

    removeLeader(leaderId) {
        const newAltLeaders = this.props.groupDetail.altLeaders;
        newAltLeaders.splice(leaderId, 1);

        const { groupDetail } = this.props;
        groupDetail.altLeaders = newAltLeaders;

        this.props.handleGroupDetailChange(groupDetail);
    }

    renderAltLeaders() {
        // use group detail
        const altLeaders = this.props.groupDetail.altLeaders.filter(
            (leader) => !leader.is_declined
        );
        if (altLeaders.length === 0) {
            return null;
        }

        return altLeaders.map((leader, index) => (
            <AlternateGroupLeader
                key={leader.key}
                leader={leader}
                index={index}
                handleAlternateLeaderChange={this.handleAlternateLeaderChange}
                isDisabled={this.props.disableAlternateLeaders}
                removeLeader={this.removeLeader}
            />
        ));
    }

    renderAltLeaderForm() {
        const altLeaders = this.props.groupDetail.altLeaders.filter(
            (leader) => !leader.is_declined
        );
        if (!altLeaders.length && this.props.disableAlternateLeaders) {
            return null;
        }

        return (
            <div>
                <div className="per-form-group">
                    <div className="per-form-group-item">
                        <div className="per-form-group-message">
                            <Icon iconName="info" />
                            <p>
                                You may select a max of {this.props.maxAlternateLeaders}{' '}
                                other people to act as alternate permit holders on your
                                trip. Please enter a valid email address for each of the
                                intended holders. They will receive an email and will need
                                to validate within <strong>72 hours</strong>. See{' '}
                                <Button
                                    appearance="link"
                                    screenReaderTextBefore="Open modal for more information on"
                                    onClick={() => this.openModal()}
                                    type="button"
                                    className="per-inline-button"
                                >
                                    Alternate Permit Holders
                                </Button>{' '}
                                for more information.
                            </p>
                        </div>
                    </div>
                    {this.renderAltLeaders()}
                </div>
                {this.state.showAddAlternateButton &&
                    !this.props.disableAlternateLeaders && (
                        <div className="rec-form-item-wrap">
                            <Button
                                appearance="link"
                                className="addOneLeaderLink"
                                screenReaderTextBefore="Add a new alternate permit holder user"
                                iconBeforeElement={<Icon iconName="add-circle" />}
                                onClick={() => this.addLeader()}
                                type="button"
                            >
                                Add alternate
                            </Button>
                        </div>
                    )}
            </div>
        );
    }

    renderVerifiedAltLeader(leader) {
        return (
            <div className="per-form-group">
                <div className="per-form-group-item">
                    <div className="per-form-group-item-header">
                        <div className="per-form-group-item-title">
                            Alternate Permit Holder #1
                        </div>
                    </div>
                    <div id="verified-alt-leader" className="rec-form-item-wrap">
                        <strong>Email:</strong> {leader.email}
                        <span className="per-verified">
                            <Icon iconName="check" />
                            Verified
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    renderAltLeader() {
        if (!get(this.props, 'conditionalComponents.alternate_group_leader')) {
            return null;
        }

        if (this.props.verifiedAlternateLeader) {
            return this.renderVerifiedAltLeader(this.props.verifiedAlternateLeader);
        }

        return this.renderAltLeaderForm();
    }

    renderAddress() {
        const { home_address: homeAddress } = this.props.groupDetail.groupLeader;

        return (
            homeAddress && (
                <div id="address" className="per-static-form-adddress">
                    {homeAddress.address1 && <div>{homeAddress.address1}</div>}
                    {homeAddress.address2 && <div>{homeAddress.address2}</div>}
                    {homeAddress.city && homeAddress.state && homeAddress.zip_code && (
                        <div>
                            {homeAddress.city}, {homeAddress.state} {homeAddress.zip_code}
                        </div>
                    )}
                </div>
            )
        );
    }

    renderContactReadOnly() {
        const { groupLeader: account } = this.props.groupDetail;
        return (
            <FlexCol md={6}>
                <div>
                    <label htmlFor="name">Name</label>
                    <span className="per-static-form-item" id="name">
                        {`${account.first_name} ${account.last_name}`}
                    </span>
                </div>
                <div>
                    <label htmlFor="email">Email</label>
                    <span className="per-static-form-item" id="email">
                        {`${account.email}`}
                    </span>
                </div>
                <div>
                    <label htmlFor="phone">Phone</label>
                    <span className="per-static-form-item" id="phone">
                        {`${account.phone1 ? account.phone1.phone : ''}`}
                    </span>
                </div>
            </FlexCol>
        );
    }

    renderAddressReadOnly() {
        return (
            <FlexCol md={6}>
                <label htmlFor="address">Address</label>
                {this.renderAddress()}
            </FlexCol>
        );
    }

    renderContactForm() {
        const { groupLeader: account } = this.props.groupDetail;

        const address1 = get(account, 'home_address.address1');
        const address2 = get(account, 'home_address.address2');
        const city = get(account, 'home_address.city');
        const state = get(account, 'home_address.state');
        const zip = get(account, 'home_address.zip_code');
        const phone = get(account, 'cell_phone');

        const {
            invalidFirstName,
            invalidLastName,
            invalidEmail,
            invalidPhone,
            invalidAddress1,
            invalidCity,
            invalidState,
            invalidZipCode,
        } = this.props.phValidations;

        return (
            <div>
                <FlexRow>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                id="first-name"
                                type="text"
                                label="First Name"
                                value={`${account.first_name}`}
                                isInvalid={invalidFirstName}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.first_name',
                                        e.target.value
                                    )
                                }
                                isDisabled={
                                    this.props.isPermitHolderNameDisabled ||
                                    this.props.isPermitHolderDisabled
                                }
                                isRequired
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                id="last-name"
                                type="text"
                                label="Last Name"
                                value={`${account.last_name}`}
                                isInvalid={invalidLastName}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.last_name',
                                        e.target.value
                                    )
                                }
                                isDisabled={
                                    this.props.isPermitHolderNameDisabled ||
                                    this.props.isPermitHolderDisabled
                                }
                                isRequired
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="text"
                                id="address1"
                                label="Street Address"
                                value={`${address1}`}
                                isInvalid={invalidAddress1}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.home_address.address1',
                                        e.target.value
                                    )
                                }
                                isDisabled={this.props.isPermitHolderDisabled}
                                isRequired
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="text"
                                id="address2"
                                label="Apt or Suite Number"
                                value={`${address2}`}
                                isDisabled={this.props.isPermitHolderDisabled}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.home_address.address2',
                                        e.target.value
                                    )
                                }
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="text"
                                id="city"
                                label="City"
                                value={`${city}`}
                                isInvalid={invalidCity}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.home_address.city',
                                        e.target.value
                                    )
                                }
                                isDisabled={this.props.isPermitHolderDisabled}
                                isRequired
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={3}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="text"
                                id="state"
                                label="State"
                                value={`${state}`}
                                isInvalid={invalidState}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.home_address.state',
                                        e.target.value
                                    )
                                }
                                isDisabled={this.props.isPermitHolderDisabled}
                                isRequired
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={3}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="text"
                                id="zip"
                                label="Zip Code"
                                value={`${zip}`}
                                isInvalid={invalidZipCode}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.home_address.zip_code',
                                        e.target.value
                                    )
                                }
                                isDisabled={this.props.isPermitHolderDisabled}
                                isRequired
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
                <FlexRow>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <TextField
                                type="text"
                                id="email"
                                label="Email"
                                value={`${account.email}`}
                                isInvalid={invalidEmail}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.email',
                                        e.target.value
                                    )
                                }
                                isDisabled
                                isRequired
                            />
                        </div>
                    </FlexCol>
                    <FlexCol md={6}>
                        <div className="rec-form-item-wrap">
                            <PhoneNumberField
                                type="text"
                                id="phone"
                                label="Phone"
                                value={`${phone}`}
                                isInvalid={invalidPhone}
                                onChange={(e) =>
                                    this.handleLeaderDetailsChange(
                                        'groupLeader.cell_phone',
                                        e.target.value
                                    )
                                }
                                isDisabled={this.props.isPermitHolderDisabled}
                                isRequired
                            />
                        </div>
                    </FlexCol>
                </FlexRow>
                {!this.props.isPermitHolderDisabled && (
                    <div className="rec-form-check-wrap">
                        <Checkbox
                            id="save-leader-checkbox"
                            className="rec-input-hide"
                            value="controlled"
                            label="Save above changes to my account."
                            isChecked={this.props.saveLeader}
                            onChange={() =>
                                this.props.handleSaveLeaderChange(!this.props.saveLeader)
                            }
                        />
                        <span className="rec-input-checkbox" />
                    </div>
                )}
            </div>
        );
    }

    renderGroupLeader() {
        return (
            <div>
                <FlexRow>
                    {this.props.readOnly && this.renderContactReadOnly()}
                    {this.props.readOnly && this.renderAddressReadOnly()}
                </FlexRow>
                {this.props.readOnly || this.renderContactForm()}

                <StyledModal
                    size="md"
                    isOpen={this.state.modalIsOpen}
                    heading="Alternate Permit Holders"
                    onRequestClose={this.closeModal}
                >
                    <Stack className="rec-modal-content-wrap">
                        <Heading headingLevel={2} appearance="h6">
                            Why An Alternate?
                        </Heading>
                        <Text>
                            The permit holder is required to be present on the entire trip
                            for this permit to be valid. In the event that the permit
                            holder is unavailable, alternate permit holder can be
                            designated at the time of reservation.
                        </Text>
                        <Heading headingLevel={2} appearance="h6">
                            How to Add an Alternate
                        </Heading>
                        <Text>
                            Enter an email address for an alternate permit holder. The
                            alternate permit holder must respond and accept being an
                            alternate within <strong>72 hours</strong> or this permit will
                            not have an alternate. If the alternate does not have a
                            recreation.gov account, they will be required to create an
                            account.
                        </Text>
                        <Heading headingLevel={2} appearance="h6">
                            Alternate Permit Holder Considerations
                        </Heading>
                        <Text>
                            Being an alternate carries the same responsibilities as the
                            primary permit holder. If there are seasonal reservations
                            restrictions, these will apply to the alternate as if they are
                            the primary permit holder. In other words, an alternate may
                            not be able to reserve another permit or be an alternate for
                            another permit at this facility for this season.
                        </Text>
                    </Stack>
                </StyledModal>
            </div>
        );
    }

    render() {
        return (
            <div className="per-group-details-form per-section-group">
                {this.props.validatingAlternateHolder ? (
                    <p>
                        Please validate your contact information below. For this
                        reservation, you will not be able to change this information
                        later.
                    </p>
                ) : (
                    <div className="rec-order-detail-subsection-title">Permit Holder</div>
                )}
                {this.renderGroupLeader()}
                {this.renderAltLeader()}
            </div>
        );
    }
}

const defaultProps = {
    maxAlternateLeaders: 1,
    readOnly: false,
    validatingAlternateHolder: false,
    phValidations: {},
};

GroupDetails.propTypes = {
    groupDetail: PropTypes.shape({
        groupLeaderType: PropTypes.string,
        otherLeader: PropTypes.shape({
            fName: PropTypes.string,
            mName: PropTypes.string,
            lName: PropTypes.string,
        }),
        altLeaders: PropTypes.arrayOf(PropTypes.shape({})),
        groupLeader: PropTypes.shape({
            first_name: PropTypes.string,
            last_name: PropTypes.string,
            email: PropTypes.string,
            home_phone: PropTypes.string,
            home_address: PropTypes.shape({
                address1: PropTypes.string,
                address2: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                zip_code: PropTypes.string,
            }),
        }),
    }),
    isPermitHolderDisabled: PropTypes.bool,
    isPermitHolderNameDisabled: PropTypes.bool,
    handleGroupDetailChange: PropTypes.func,
    conditionalComponents: PropTypes.shape({
        alternate_group_leader: PropTypes.bool,
    }),
    maxAlternateLeaders: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    validatingAlternateHolder: PropTypes.bool,
    verifiedAlternateLeader: PropTypes.shape({}),
    disableAlternateLeaders: PropTypes.bool,
    readOnly: PropTypes.bool,
    saveLeader: PropTypes.bool,
    handleSaveLeaderChange: PropTypes.func,
    phValidations: PropTypes.shape({
        invalidFirstName: PropTypes.bool,
        invalidLastName: PropTypes.bool,
        invalidEmail: PropTypes.bool,
        invalidPhone: PropTypes.bool,
        invalidAddress1: PropTypes.bool,
        invalidCity: PropTypes.bool,
        invalidState: PropTypes.bool,
        invalidZipCode: PropTypes.bool,
    }),
};
GroupDetails.defaultProps = defaultProps;
